import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Package, Plus, MagnifyingGlass, Funnel, PencilSimple, Trash, Thermometer, ArrowsClockwise, Eye, X } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import SampleDetailDialog from '../components/SampleDetailDialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import ESignature from '../components/ESignature';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  recibido: 'bg-blue-50 text-blue-700 border-blue-200',
  en_uso: 'bg-green-50 text-green-700 border-green-200',
  agotado: 'bg-stone-50 text-stone-600 border-stone-300',
  devuelto: 'bg-amber-50 text-amber-700 border-amber-200',
  destruido: 'bg-red-50 text-red-600 border-red-200',
};

const storageLabels = {
  es: { ambiente: 'T° Ambiente', refrigerado: 'Refrigerado (2-8°C)', congelado: 'Congelado (-20°C)', ultra_congelado: 'Ultra-congelado (-80°C)' },
  en: { ambiente: 'Room temp', refrigerado: 'Refrigerated (2-8°C)', congelado: 'Frozen (-20°C)', ultra_congelado: 'Ultra-frozen (-80°C)' },
};

const emptyForm = { study_id: '', product_name: '', manufacturer_lot: '', blind_code: '', is_blind: false, quantity_received: 0, unit_weight: '', measure_mode: 'peso', reception_date: '', expiry_date: '', storage_condition: 'ambiente', reception_temperature: '', location: '', status: 'recibido', notes: '' };

function SamplesTab({ lang, studies, navigate }) {
  // URS-F448: solo admins pueden renombrar el ID de una muestra.
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  const [samples, setSamples] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStudy, setFilterStudy] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [originalSampleId, setOriginalSampleId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [movements, setMovements] = useState([]);
  const [dispensations, setDispensations] = useState([]);
  const [showDispense, setShowDispense] = useState(null);
  const [dispForm, setDispForm] = useState({ volunteer_id: '', visit_code: '', quantity: 1, date: '', notes: '' });
  const [studyVols, setStudyVols] = useState([]);
  const [studyVisits, setStudyVisits] = useState([]);
  const [showMovement, setShowMovement] = useState(null);
  const [movForm, setMovForm] = useState({ type: 'devolucion', quantity: 0, date: '', notes: '', destination: '', method: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const lb = lang === 'es'
    ? { new: 'Nueva Muestra', edit: 'Editar Muestra', search: 'Buscar por producto, lote o ID...', no_samples: 'No hay muestras registradas', id: 'ID', product: 'Producto', lot: 'Lote', study: 'Estudio', client: 'Cliente', stock: 'Stock', status: 'Estado', expiry: 'Caducidad', storage: 'Almacenamiento', save: 'Guardar', cancel: 'Cancelar', all: 'Todos', detail: 'Detalle de muestra', history: 'Historial', dispense: 'Dispensar', movement: 'Movimiento', qty_received: 'Cantidad recibida', unit_weight: 'Peso por unidad (g)', qty_dispensed: 'Dispensado', remaining: 'Restante', blind: 'Ciego', blind_code: 'Codigo ciego', reception_date: 'Fecha recepcion', expiry_date: 'Caducidad', reception_temp: 'T° recepcion', location: 'Ubicacion', volunteer: 'Voluntario', visit: 'Visita', quantity: 'Cantidad', date: 'Fecha', notes: 'Notas', dispensed_by: 'Dispensado por', devolucion: 'Devolucion al cliente', destruccion: 'Destruccion', destination: 'Destinatario', method_label: 'Metodo destruccion', confirm_delete: 'Eliminar muestra?', recibido: 'Recibido', en_uso: 'En uso', agotado: 'Agotado', devuelto: 'Devuelto', destruido: 'Destruido', dispense_muestroteca: 'Dispensar a muestroteca', muestroteca_col: 'Muestroteca', dispense_to_muestroteca_btn: 'Dispensar a muestroteca' }
    : { new: 'New Sample', edit: 'Edit Sample', search: 'Search by product, lot or ID...', no_samples: 'No samples registered', id: 'ID', product: 'Product', lot: 'Lot', study: 'Study', client: 'Client', stock: 'Stock', status: 'Status', expiry: 'Expiry', storage: 'Storage', save: 'Save', cancel: 'Cancel', all: 'All', detail: 'Sample detail', history: 'History', dispense: 'Dispense', movement: 'Movement', qty_received: 'Qty received', unit_weight: 'Unit weight (g)', qty_dispensed: 'Dispensed', remaining: 'Remaining', blind: 'Blind', blind_code: 'Blind code', reception_date: 'Reception date', expiry_date: 'Expiry date', reception_temp: 'Reception temp', location: 'Location', volunteer: 'Volunteer', visit: 'Visit', quantity: 'Quantity', date: 'Date', notes: 'Notes', dispensed_by: 'Dispensed by', devolucion: 'Return to client', destruccion: 'Destruction', destination: 'Destination', method_label: 'Destruction method', confirm_delete: 'Delete sample?', recibido: 'Received', en_uso: 'In use', agotado: 'Depleted', devuelto: 'Returned', destruido: 'Destroyed', dispense_muestroteca: 'Dispense to muestroteca', muestroteca_col: 'Muestroteca', dispense_to_muestroteca_btn: 'Dispense to muestroteca' };

  const studyMap = {};
  studies.forEach(s => { studyMap[s.study_id] = s; });

  const load = () => {
    const params = new URLSearchParams();
    if (filterStudy !== 'all') params.append('study_id', filterStudy);
    if (filterStatus !== 'all') params.append('status', filterStatus);
    axios.get(`${API_URL}/api/samples?${params}`, { withCredentials: true })
      .then(r => setSamples(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filterStudy, filterStatus]);

  const filtered = samples.filter(s =>
    (s.product_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.manufacturer_lot || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.sample_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.blind_code || '').toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => { setEditing(null); setOriginalSampleId(null); setForm({ ...emptyForm }); setShowForm(true); };
  const openEdit = (s) => {
    setEditing(s.sample_id);
    setOriginalSampleId(s.sample_id);
    setForm({ sample_id: s.sample_id, study_id: s.study_id, product_name: s.product_name, manufacturer_lot: s.manufacturer_lot || '', blind_code: s.blind_code || '', is_blind: s.is_blind || false, quantity_received: s.quantity_received, unit_weight: s.unit_weight || '', measure_mode: s.measure_mode || 'peso', reception_date: s.reception_date || '', expiry_date: s.expiry_date || '', storage_condition: s.storage_condition || 'ambiente', reception_temperature: s.reception_temperature || '', location: s.location || '', status: s.status, notes: s.notes || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.sample_id?.trim() || !form.product_name) { toast.error(lang === 'es' ? 'ID y producto obligatorios' : 'ID and product required'); return; }
    setSaving(true);
    try {
      if (editing) {
        // URS-F448: si el admin cambio el ID, viaja como `new_sample_id`
        // para disparar el rename cascade en el backend.
        const payload = { ...form };
        if (isAdmin && originalSampleId && form.sample_id.trim() !== originalSampleId) {
          payload.new_sample_id = form.sample_id.trim();
        }
        // URS-F448b: si el sample_id ORIGINAL contiene "/" u otros caracteres
        // problematicos para el path routing (barras, ingress bloquea %2F),
        // usamos el endpoint body-based /samples/action-update en vez del PUT
        // por path. Asi el rename funciona igualmente sobre IDs tipo
        // "C/CDQ-48-01-x/yy".
        const idHasSlash = /[/\s?#%]/.test(originalSampleId || editing);
        if (idHasSlash) {
          // OJO orden: `sample_id` va DESPUES del spread para que apunte al
          // ID ORIGINAL (no al nuevo que ya esta en `payload.sample_id`).
          // El backend usa `sample_id` para localizar la muestra y
          // `new_sample_id` para renombrarla.
          await axios.post(`${API_URL}/api/samples/action-update`, { ...payload, sample_id: originalSampleId || editing }, { withCredentials: true });
        } else {
          await axios.put(`${API_URL}/api/samples/${originalSampleId || editing}`, payload, { withCredentials: true });
        }
      }
      else { await axios.post(`${API_URL}/api/samples`, form, { withCredentials: true }); }
      toast.success(lang === 'es' ? 'Muestra guardada' : 'Sample saved');
      setShowForm(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (id, signature) => {
    try { await axios.delete(`${API_URL}/api/samples/${id}`, { withCredentials: true, data: { signature } }); toast.success(lang === 'es' ? 'Muestra eliminada' : 'Sample deleted'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  const openDetail = async (s) => {
    setDetail(s);
    const [mr, dr] = await Promise.all([
      axios.get(`${API_URL}/api/samples/${s.sample_id}/movements`, { withCredentials: true }),
      axios.get(`${API_URL}/api/samples/${s.sample_id}/dispensations`, { withCredentials: true }),
    ]);
    setMovements(mr.data);
    setDispensations(dr.data);
  };

  const openDispense = async (s) => {
    setShowDispense(s);
    setDispForm({ volunteer_id: '', visit_code: '', quantity: 1, date: new Date().toISOString().split('T')[0], notes: '', to_muestroteca: false });
    const [vr, visr] = await Promise.all([
      axios.get(`${API_URL}/api/studies/${s.study_id}/volunteers`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies/${s.study_id}/visits`, { withCredentials: true }),
    ]);
    // URS-F344b: incluir tambien completados en el selector
    setStudyVols(vr.data.filter(v => v.status === 'incluido' || v.status === 'completado'));
    setStudyVisits(visr.data);
  };

  const handleDispense = async () => {
    // URS-F349: si va a muestroteca no exigimos voluntario.
    if (!dispForm.to_muestroteca && !dispForm.volunteer_id) {
      toast.error(lang === 'es' ? 'Selecciona voluntario o marca "Dispensar a muestroteca"' : 'Select a volunteer or check "Dispense to muestroteca"');
      return;
    }
    setSaving(true);
    try {
      const payload = { sample_id: showDispense.sample_id, ...dispForm };
      if (payload.to_muestroteca) {
        payload.volunteer_id = null;
        payload.visit_code = '';
      }
      await axios.post(`${API_URL}/api/dispensations`, payload, { withCredentials: true });
      toast.success(lang === 'es'
        ? (dispForm.to_muestroteca ? 'Muestra dispensada a muestroteca' : 'Muestra dispensada')
        : (dispForm.to_muestroteca ? 'Sample dispensed to muestroteca' : 'Sample dispensed'));
      setShowDispense(null); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const openMovement = (s) => {
    setShowMovement(s);
    setMovForm({ type: 'devolucion', quantity: 0, date: new Date().toISOString().split('T')[0], notes: '', destination: '', method: '' });
  };

  const handleMovement = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/sample-movements`, { sample_id: showMovement.sample_id, ...movForm }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Movimiento registrado' : 'Movement recorded');
      setShowMovement(null); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="sample-search" placeholder={lb.search} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-stone-200 text-sm h-8" />
          </div>
          <Select value={filterStudy} onValueChange={setFilterStudy}>
            <SelectTrigger data-testid="filter-study" className="w-40 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lb.all}</SelectItem>
              {studies.map(s => (<SelectItem key={s.study_id} value={s.study_id}>{s.code}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-32 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lb.all}</SelectItem>
              {['recibido', 'en_uso', 'agotado', 'devuelto', 'destruido'].map(s => (<SelectItem key={s} value={s}>{lb[s]}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="create-sample-btn" onClick={openNew} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 h-8 text-sm">
          <Plus size={14} />{lb.new}
        </Button>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><Package size={40} className="text-stone-300 mx-auto mb-3" /><p className="text-sm text-stone-500">{lb.no_samples}</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-28">{lb.id}</TableHead>
              <TableHead>{lb.product}</TableHead>
              <TableHead className="w-28">{lb.lot}</TableHead>
              <TableHead className="w-32">{lb.study}</TableHead>
              <TableHead className="w-32">{lb.client}</TableHead>
              <TableHead className="w-28">{lb.stock}</TableHead>
              <TableHead className="w-28">{lb.expiry}</TableHead>
              <TableHead className="w-32">{lb.storage}</TableHead>
              <TableHead className="w-24">{lb.muestroteca_col}</TableHead>
              <TableHead className="w-24">{lb.status}</TableHead>
              <TableHead className="w-32"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(s => {
                const isExpiring = s.expiry_date && new Date(s.expiry_date) < new Date(Date.now() + 30 * 86400000);
                return (
                  <TableRow key={s.sample_id} data-testid={`sample-row-${s.sample_id}`} className="hover:bg-stone-50">
                    <TableCell className="font-mono text-xs text-stone-600">{s.sample_id}{s.is_blind && <Badge variant="outline" className="text-[9px] ml-1 bg-violet-50 text-violet-600 border-violet-200">{lb.blind}</Badge>}</TableCell>
                    <TableCell className="text-sm font-medium text-stone-900">{s.product_name}</TableCell>
                    <TableCell className="text-xs font-mono text-stone-600">{s.manufacturer_lot || '-'}</TableCell>
                    <TableCell className="text-xs"><button onClick={() => navigate(`/studies/${s.study_id}`)} className="text-[#1f3e80] hover:underline font-medium">{s.study_code || studyMap[s.study_id]?.code || s.study_id}</button></TableCell>
                    <TableCell className="text-xs text-stone-600 truncate max-w-[120px]">{s.client_name || '-'}</TableCell>
                    <TableCell className="text-sm"><span className="font-semibold text-stone-900">{s.stock_remaining}</span><span className="text-xs text-stone-400">/{s.quantity_received}</span></TableCell>
                    <TableCell className={`text-xs ${isExpiring ? 'text-red-600 font-semibold' : 'text-stone-600'}`}>{s.expiry_date || '-'}</TableCell>
                    <TableCell className="text-xs text-stone-600">{storageLabels[lang]?.[s.storage_condition] || s.storage_condition}</TableCell>
                    <TableCell data-testid={`muestroteca-cell-${s.sample_id}`}>
                      {s.has_muestroteca
                        ? <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-300">SI</Badge>
                        : <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-500 border-stone-200">NO</Badge>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColors[s.status] || ''}`}>{lb[s.status] || s.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`view-sample-${s.sample_id}`} onClick={() => openDetail(s)}><Eye size={14} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" data-testid={`dispense-${s.sample_id}`} onClick={() => openDispense(s)} disabled={s.stock_remaining <= 0}><Plus size={14} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-sample-${s.sample_id}`} onClick={() => openEdit(s)}><PencilSimple size={14} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`movement-${s.sample_id}`} onClick={() => openMovement(s)}><ArrowsClockwise size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Sample Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle><DialogDescription></DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.id} <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="sample-id-input"
                  value={form.sample_id}
                  onChange={e => setForm(p => ({ ...p, sample_id: e.target.value }))}
                  placeholder={lang === 'es' ? 'Ej: ALM-0001' : 'E.g. ALM-0001'}
                  disabled={!!editing && !isAdmin}
                  className={editing && !isAdmin ? 'bg-stone-50 text-stone-500 cursor-not-allowed' : ''}
                  required
                />
                {editing && isAdmin && originalSampleId && form.sample_id !== originalSampleId && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-1" data-testid="sample-id-rename-warning">
                    {lang === 'es'
                      ? `⚠ Se renombrara "${originalSampleId}" -> "${form.sample_id}" en muestra, dispensaciones, movimientos y sondas.`
                      : `⚠ "${originalSampleId}" will be renamed -> "${form.sample_id}" across sample, dispensations, movements and probes.`}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>{lb.study}</Label>
                <StudyCombobox
                  studies={studies}
                  value={form.study_id}
                  onChange={v => setForm(p => ({ ...p, study_id: v }))}
                  lang={lang}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{lb.product} <span className="text-red-500">*</span></Label>
                <Input data-testid="sample-product-input" value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))} />
              </div>
              <div className="space-y-1.5"><Label>{lb.lot}</Label><Input data-testid="sample-lot-input" value={form.manufacturer_lot} onChange={e => setForm(p => ({ ...p, manufacturer_lot: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{lb.qty_received}</Label><Input data-testid="sample-qty-input" type="number" value={form.quantity_received} onChange={e => setForm(p => ({ ...p, quantity_received: parseInt(e.target.value) || 0 }))} /></div>
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Tipo de medicion' : 'Measurement type'}</Label>
                <Select value={form.measure_mode || 'peso'} onValueChange={v => setForm(p => ({ ...p, measure_mode: v }))}>
                  <SelectTrigger data-testid="sample-measure-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peso">{lang === 'es' ? 'Peso (g) — Cremas, liquidos' : 'Weight (g) — Creams, liquids'}</SelectItem>
                    <SelectItem value="unidades">{lang === 'es' ? 'Unidades (uds) — Pastillas, ampollas' : 'Units — Pills, ampoules'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{form.measure_mode === 'unidades' ? (lang === 'es' ? 'Cantidad inicial por voluntario (uds)' : 'Initial quantity per volunteer') : (lang === 'es' ? 'Peso por unidad (g)' : 'Unit weight (g)')}</Label><Input data-testid="sample-unit-weight-input" value={form.unit_weight} onChange={e => setForm(p => ({ ...p, unit_weight: e.target.value }))} placeholder={form.measure_mode === 'unidades' ? '30' : '50.0'} /></div>
              <div className="space-y-1.5"><Label>{lb.reception_date}</Label><Input data-testid="sample-reception-date" type="date" value={form.reception_date} onChange={e => setForm(p => ({ ...p, reception_date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{lb.expiry_date}</Label><Input data-testid="sample-expiry-date" type="date" value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{lb.storage}</Label>
                <Select value={form.storage_condition} onValueChange={v => setForm(p => ({ ...p, storage_condition: v }))}>
                  <SelectTrigger data-testid="sample-storage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(storageLabels[lang] || storageLabels.es).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{lb.reception_temp}</Label><Input data-testid="sample-temp-input" value={form.reception_temperature} onChange={e => setForm(p => ({ ...p, reception_temperature: e.target.value }))} placeholder="5.2°C" /></div>
              <div className="space-y-1.5"><Label>{lb.location}</Label><Input data-testid="sample-location-input" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder={lang === 'es' ? 'Nevera A - Estante 2' : 'Fridge A - Shelf 2'} /></div>
              <div className="space-y-1.5"><Label>{lb.status}</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="sample-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{['recibido', 'en_uso', 'agotado', 'devuelto', 'destruido'].map(s => (<SelectItem key={s} value={s}>{lb[s]}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Switch data-testid="sample-blind-switch" checked={form.is_blind} onCheckedChange={v => setForm(p => ({ ...p, is_blind: v }))} /><Label>{lb.blind}</Label></div>
              {form.is_blind && <div className="flex-1 space-y-1.5"><Label>{lb.blind_code}</Label><Input data-testid="sample-blind-code" value={form.blind_code} onChange={e => setForm(p => ({ ...p, blind_code: e.target.value }))} /></div>}
            </div>
            <div className="space-y-1.5"><Label>{lb.notes}</Label><Textarea data-testid="sample-notes-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[50px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>{lb.cancel}</Button>
            <Button data-testid="save-sample-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <SampleDetailDialog
        sample={detail}
        movements={movements}
        dispensations={dispensations}
        studyCode={detail ? studyMap[detail.study_id]?.code : undefined}
        lang={lang}
        onClose={() => setDetail(null)}
      />

      {/* Dispense Dialog */}
      <Dialog open={!!showDispense} onOpenChange={() => setShowDispense(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{lb.dispense}: {showDispense?.sample_id}</DialogTitle><DialogDescription></DialogDescription></DialogHeader>
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer p-2.5 bg-purple-50 border border-purple-200 rounded-sm hover:bg-purple-100 transition-colors">
              <Checkbox
                data-testid="dispense-to-muestroteca"
                checked={dispForm.to_muestroteca}
                onCheckedChange={(v) => setDispForm(p => ({ ...p, to_muestroteca: !!v, volunteer_id: v ? '' : p.volunteer_id, visit_code: v ? '' : p.visit_code }))}
              />
              <span className="text-sm font-medium text-purple-900">{lb.dispense_muestroteca}</span>
            </label>
            <div className="space-y-1.5"><Label className={dispForm.to_muestroteca ? 'opacity-40' : ''}>{lb.volunteer} {!dispForm.to_muestroteca && <span className="text-red-500">*</span>}</Label>
              <Select value={dispForm.volunteer_id || '_n'} onValueChange={v => setDispForm(p => ({ ...p, volunteer_id: v === '_n' ? '' : v }))} disabled={dispForm.to_muestroteca}>
                <SelectTrigger data-testid="dispense-volunteer-select"><SelectValue /></SelectTrigger>
                <SelectContent>{studyVols.map(v => (<SelectItem key={v.volunteer_id} value={v.volunteer_id}>{v.study_volunteer_code} — {v.volunteer_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className={dispForm.to_muestroteca ? 'opacity-40' : ''}>{lb.visit}</Label>
              <Select value={dispForm.visit_code || '_n'} onValueChange={v => setDispForm(p => ({ ...p, visit_code: v === '_n' ? '' : v }))} disabled={dispForm.to_muestroteca}>
                <SelectTrigger data-testid="dispense-visit-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="_n">-</SelectItem>{studyVisits.map(v => (<SelectItem key={v.visit_id} value={v.code}>{v.code} — {v.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lb.quantity}</Label><Input data-testid="dispense-qty" type="number" min="1" value={dispForm.quantity} onChange={e => setDispForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} /></div>
              <div className="space-y-1.5"><Label>{lb.date}</Label><Input data-testid="dispense-date" type="date" value={dispForm.date} onChange={e => setDispForm(p => ({ ...p, date: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>{lb.notes}</Label><Textarea data-testid="dispense-notes" value={dispForm.notes} onChange={e => setDispForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[40px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDispense(null)}>{lb.cancel}</Button>
            <Button data-testid="confirm-dispense-btn" onClick={handleDispense} disabled={saving} className={(dispForm.to_muestroteca ? 'bg-purple-700 hover:bg-purple-800' : 'bg-[#1f3e80] hover:bg-[#2a4f8a]') + ' text-white'}>{saving ? '...' : (dispForm.to_muestroteca ? lb.dispense_to_muestroteca_btn : lb.dispense)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog (return/destroy) */}
      <Dialog open={!!showMovement} onOpenChange={() => setShowMovement(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{lb.movement}: {showMovement?.sample_id}</DialogTitle><DialogDescription></DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{lang === 'es' ? 'Tipo' : 'Type'}</Label>
              <Select value={movForm.type} onValueChange={v => setMovForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="devolucion">{lb.devolucion}</SelectItem>
                  <SelectItem value="destruccion">{lb.destruccion}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lb.quantity}</Label><Input data-testid="movement-qty" type="number" value={movForm.quantity} onChange={e => setMovForm(p => ({ ...p, quantity: parseInt(e.target.value) || 0 }))} /></div>
              <div className="space-y-1.5"><Label>{lb.date}</Label><Input data-testid="movement-date" type="date" value={movForm.date} onChange={e => setMovForm(p => ({ ...p, date: e.target.value }))} /></div>
            </div>
            {movForm.type === 'devolucion' && <div className="space-y-1.5"><Label>{lb.destination}</Label><Input data-testid="movement-destination" value={movForm.destination} onChange={e => setMovForm(p => ({ ...p, destination: e.target.value }))} /></div>}
            {movForm.type === 'destruccion' && <div className="space-y-1.5"><Label>{lb.method_label}</Label><Input data-testid="movement-method" value={movForm.method} onChange={e => setMovForm(p => ({ ...p, method: e.target.value }))} /></div>}
            <div className="space-y-1.5"><Label>{lb.notes}</Label><Textarea data-testid="movement-notes" value={movForm.notes} onChange={e => setMovForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[40px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMovement(null)}>{lb.cancel}</Button>
            <Button data-testid="confirm-movement-btn" onClick={handleMovement} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemperatureTab({ lang }) {
  const [logs, setLogs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filterLoc, setFilterLoc] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ location: '', temperature: '', date: '', time: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const lb = lang === 'es'
    ? { title: 'Registro de Temperatura', new: 'Nuevo Registro', location: 'Ubicacion', temperature: 'Temperatura (°C)', date: 'Fecha', time: 'Hora', notes: 'Notas', recorded_by: 'Registrado por', save: 'Guardar', cancel: 'Cancelar', all: 'Todas', no_logs: 'No hay registros de temperatura' }
    : { title: 'Temperature Log', new: 'New Log', location: 'Location', temperature: 'Temperature (°C)', date: 'Date', time: 'Time', notes: 'Notes', recorded_by: 'Recorded by', save: 'Save', cancel: 'Cancel', all: 'All', no_logs: 'No temperature logs' };

  const load = () => {
    const params = filterLoc !== 'all' ? `?location=${filterLoc}` : '';
    Promise.all([
      axios.get(`${API_URL}/api/temperature-logs${params}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/warehouse/locations`, { withCredentials: true }),
    ]).then(([lr, locr]) => {
      setLogs(lr.data);
      setLocations(locr.data);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [filterLoc]);

  const handleSave = async () => {
    if (!form.temperature || !form.date || !form.location) { toast.error(lang === 'es' ? 'Ubicacion, temperatura y fecha obligatorios' : 'Location, temp and date required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/temperature-logs`, form, { withCredentials: true });
      toast.success(lang === 'es' ? 'Registro guardado' : 'Log saved');
      setShowForm(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={filterLoc} onValueChange={setFilterLoc}>
          <SelectTrigger data-testid="temp-filter-location" className="w-48 h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lb.all}</SelectItem>
            {locations.map(l => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button data-testid="create-temp-log-btn" onClick={() => { setForm({ location: '', temperature: '', date: new Date().toISOString().split('T')[0], time: new Date().toTimeString().slice(0, 5), notes: '' }); setShowForm(true); }} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2 h-8 text-sm">
          <Thermometer size={14} />{lb.new}
        </Button>
      </div>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-12 text-center"><Thermometer size={40} className="text-stone-300 mx-auto mb-3" /><p className="text-sm text-stone-500">{lb.no_logs}</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>{lb.date}</TableHead>
              <TableHead>{lb.time}</TableHead>
              <TableHead>{lb.location}</TableHead>
              <TableHead>{lb.temperature}</TableHead>
              <TableHead>{lb.notes}</TableHead>
              <TableHead>{lb.recorded_by}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logs.map(l => (
                <TableRow key={l.log_id} data-testid={`temp-log-${l.log_id}`}>
                  <TableCell className="text-sm">{l.date}</TableCell>
                  <TableCell className="text-sm font-mono">{l.time || '-'}</TableCell>
                  <TableCell className="text-sm">{l.location}</TableCell>
                  <TableCell className="text-sm font-mono font-semibold">{l.temperature}°C</TableCell>
                  <TableCell className="text-xs text-stone-500">{l.notes || '-'}</TableCell>
                  <TableCell className="text-xs text-stone-400">{l.recorded_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{lb.new}</DialogTitle><DialogDescription></DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{lb.location} <span className="text-red-500">*</span></Label><Input data-testid="temp-location-input" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder={lang === 'es' ? 'Nevera A' : 'Fridge A'} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label>{lb.temperature} <span className="text-red-500">*</span></Label><Input data-testid="temp-value-input" value={form.temperature} onChange={e => setForm(p => ({ ...p, temperature: e.target.value }))} placeholder="4.5" /></div>
              <div className="space-y-1.5"><Label>{lb.date} <span className="text-red-500">*</span></Label><Input data-testid="temp-date-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{lb.time}</Label><Input data-testid="temp-time-input" type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>{lb.notes}</Label><Textarea data-testid="temp-notes-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[40px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>{lb.cancel}</Button>
            <Button data-testid="save-temp-log-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Warehouse() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/studies`, { withCredentials: true }).then(r => setStudies(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="warehouse-page">
      <div className="flex items-center gap-3">
        <Package size={28} weight="duotone" className="text-[#1f3e80]" />
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{t('warehouse')}</h1>
      </div>
      <Tabs defaultValue="samples" className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="samples" data-testid="warehouse-tab-samples">{lang === 'es' ? 'Muestras' : 'Samples'}</TabsTrigger>
          <TabsTrigger value="temperature" data-testid="warehouse-tab-temperature">{lang === 'es' ? 'Temperatura' : 'Temperature'}</TabsTrigger>
        </TabsList>
        <TabsContent value="samples" className="mt-6"><SamplesTab lang={lang} studies={studies} navigate={navigate} /></TabsContent>
        <TabsContent value="temperature" className="mt-6"><TemperatureTab lang={lang} /></TabsContent>
      </Tabs>
    </div>
  );
}


function StudyCombobox({ studies, value, onChange, lang }) {
  const [open, setOpen] = useState(false);
  const selected = studies.find(s => s.study_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="sample-study-select"
          className="w-full justify-between font-normal text-left h-9 bg-white border-stone-200"
        >
          <span className="truncate">
            {selected ? `${selected.code} — ${selected.title}` : (lang === 'es' ? 'Seleccionar estudio...' : 'Select study...')}
          </span>
          <svg width="12" height="12" viewBox="0 0 12 12" className="ml-2 shrink-0 opacity-50"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={lang === 'es' ? 'Buscar estudio...' : 'Search study...'} data-testid="sample-study-search" />
          <CommandList>
            <CommandEmpty>{lang === 'es' ? 'Sin resultados' : 'No results'}</CommandEmpty>
            {studies.map(s => (
              <CommandItem
                key={s.study_id}
                value={`${s.code} ${s.title}`}
                onSelect={() => { onChange(s.study_id); setOpen(false); }}
                data-testid={`sample-study-option-${s.study_id}`}
              >
                <span className="truncate text-xs">{s.code} — {s.title}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
